import { deepEquals } from './deepEquals'
import type { Promisable } from '@/types'

/**
 * 함수가 실행중에는 재실행 되지 않는 함수를 반환
 *
 * @param executeFunction 실행할 함수
 */
export const getExecuteOnce = <T extends (...args: any) => Promisable<any>>(
  executeFunction: T,
): ((...params: Parameters<T>) => Promisable<ReturnType<T>>) => {
  /**
   * 현재 실행중인 Promise 의 정보
   */
  const executingFunctionInfoList = new ExecutingFunctionInfoList<T>()

  return async (...params: Parameters<T>[]) => {
    const executingInfo =
      executingFunctionInfoList.getExecutingFunctionFromInfoList(params)
    if (executingInfo) {
      return executingInfo.executingFunction
    }
    const newInfo = executingFunctionInfoList.addExecutingFunctionInfo({
      params,
      executingFunction: new Promise((res) => {
        res(executeFunction(...params))
      }),
    })
    const result = await newInfo.executingFunction
    executingFunctionInfoList.removeExecutingFunctionInfo(newInfo)
    return result
  }
}

type ExecutingFunctionInfo<T extends (...args: any) => Promisable<any>> = {
  params: Parameters<T>[]
  executingFunction: Promise<ReturnType<T>>
}

/**
 * paramter 가 같은 함수들만 중복 실행을 막아야 하기 때문에
 * parameter 와 executingFunction 을 저장하는 클래스
 */
class ExecutingFunctionInfoList<T extends (...args: any) => Promisable<any>> {
  private infoList: ExecutingFunctionInfo<T>[] = []

  /**
   * paramter 가 같은 함수들만 중복 실행을 막아야 함
   */
  getExecutingFunctionFromInfoList(params: any) {
    return this.infoList.find((info) => {
      return deepEquals(info.params, params)
    })
  }
  addExecutingFunctionInfo(info: ExecutingFunctionInfo<T>) {
    this.infoList.push(info)
    return info
  }
  removeExecutingFunctionInfo(info: ExecutingFunctionInfo<T>) {
    this.infoList = this.infoList.filter((i) => i !== info)
  }
}
